import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  kicker?: string;
  children: ReactNode;
  width?: number;
  footer?: ReactNode;
};

export function Modal({ open, onClose, title, kicker, children, width = 520, footer }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute inset-0 bg-ink-800/30 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className="relative surface-frosted w-full overflow-hidden"
            style={{ maxWidth: width }}
          >
            <div className="flex items-start justify-between p-5 border-b border-cream-300">
              <div>
                {kicker && <div className="kicker kicker-brand mb-1">{kicker}</div>}
                <h3 className="serif text-[18px] text-ink-800">{title}</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-ink-400 hover:text-ink-800 hover:bg-cream-200 transition"
              >
                <X size={15} />
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto scroll-soft">{children}</div>
            {footer && <div className="px-5 py-3.5 border-t border-cream-300 flex items-center justify-end gap-2 bg-cream-50/50">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
